/**
 * One camera's live view: the frame, and everything the device says about it.
 *
 * Split out of TestConsolePage when the cabinet gained a second camera. A
 * cabinet is watched from above and below, and a pickup is judged by looking
 * at both at once — so this had to stop being a singleton. Each pane owns its
 * own frame size, because the two cameras are different sensors at different
 * aspect ratios (12 MP 4:3 above, 2 MP 16:9 below) and the overlay geometry is
 * computed from it: sharing one would put every box on the wrong pane.
 */
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { drawCorrected, makeCorrector } from "../lib/lensView";

// A colour per product, stable across frames and panes.
const PRODUCT_COLOURS = ["#22d3ee", "#f97316", "#a3e635", "#e879f9", "#facc15", "#fb7185", "#60a5fa", "#34d399"];
function productColour(id) {
  let h = 0;
  for (const ch of String(id || "")) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return PRODUCT_COLOURS[h % PRODUCT_COLOURS.length];
}

// rotation: degrees clockwise that show this camera upright to a person
// (health `cameras.display_rotation`). A camera mounted sideways on a side wall
// delivers frames whose geometry the runtime relies on as they are (door at the
// top, shelves below), so the device never rotates them: only this view does.
// The frame and everything drawn on it in frame coordinates turn together on
// one "stage"; the pane's own chips stay upright.
// large: fill the parent instead of a fixed aspect (the full-screen view).
// showBoxes / showText: the geometric layers and the words on top of them,
// separately, because at a small size the words hide what the boxes point at.
// stale: { age, why } when the pane has had no new frame for a while.
// calibration: this camera's measured lens and light (the device's
// calibration document, docs/tasks/39); corrected: show the frame straightened
// and white-balanced from it. A view only - the cabinet keeps recognising on
// the native frame, and every overlay is carried into the corrected picture
// through the same lens model (lib/lensView.js).
export default function CameraPane({
  cameraId, frameUrl, overlay, label, names = {}, rotation = 0,
  large = false, showBoxes = true, showText = true, stale = null, onExpand = null,
  calibration = null, corrected = false, onToggleCorrected = null,
}) {
  const [frameSize, setFrameSize] = useState(null);
  // The remap tables for this frame size: ~50 ms to build, so once per size.
  const corrector = useMemo(
    () => (corrected && calibration && frameSize?.w ? makeCorrector(calibration, frameSize.w, frameSize.h) : null),
    [corrected, calibration, frameSize],
  );
  const imgRef = useRef(null);
  const canvasRef = useRef(null);
  const scratchRef = useRef(null);
  const paint = useCallback(() => {
    const img = imgRef.current, canvas = canvasRef.current;
    if (!corrector || !img || !canvas || !img.complete || !img.naturalWidth) return;
    try {
      scratchRef.current = drawCorrected(img, canvas, corrector, scratchRef.current);
    } catch (e) {
      // A cross-origin frame taints the canvas; the native picture stays.
      console.warn("corrected view", e);
    }
  }, [corrector]);
  useEffect(() => { paint(); }, [paint, frameUrl]);
  const turn = ((Math.round(Number(rotation) / 90) * 90) % 360 + 360) % 360;
  const sideways = turn === 90 || turn === 270;
  // The pane's real shape, measured: the full-screen view is whatever the
  // window is, and letterboxing against an assumed shape misplaces every box.
  const paneRef = useRef(null);
  const [measured, setMeasured] = useState(null);
  useLayoutEffect(() => {
    const el = paneRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      if (width > 0 && height > 0) setMeasured(width / height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  // A sideways camera shows a portrait picture: give the pane a portrait shape.
  const paneAspect = measured || (sideways ? 3 / 4 : 16 / 9);

  // The stage is exactly the frame's rectangle, letterboxed into the pane and
  // then rotated about its centre, so frame-normalised coordinates are plain
  // percentages of it - no offset arithmetic per box.
  const stageStyle = useMemo(() => {
    if (!frameSize?.w || !frameSize?.h) return { left: 0, top: 0, width: "100%", height: "100%" };
    const a = frameSize.w / frameSize.h;
    const shown = sideways ? 1 / a : a;            // aspect of the picture as displayed
    const fitW = shown > paneAspect ? 1 : shown / paneAspect;   // fraction of pane width
    const fitH = shown > paneAspect ? paneAspect / shown : 1;   // fraction of pane height
    // The stage is laid out unrotated: a sideways one swaps its sides.
    const w = sideways ? fitH / paneAspect : fitW;
    const h = sideways ? fitW * paneAspect : fitH;
    return {
      left: `${((1 - w) / 2) * 100}%`, top: `${((1 - h) / 2) * 100}%`,
      width: `${w * 100}%`, height: `${h * 100}%`,
      transform: turn ? `rotate(${turn}deg)` : undefined, transformOrigin: "50% 50%",
    };
  }, [frameSize, sideways, paneAspect, turn]);
  // Labels on boxes turn back so they read level whatever the stage does.
  const levelLabel = turn ? { transform: `rotate(${-turn}deg)`, transformOrigin: "0 100%" } : undefined;
  // Box labels: hidden with the texts, the box itself stays.
  const labelStyle = showText ? levelLabel : { display: "none" };
  // The overlay names the camera that took the frame. A pane showing another
  // camera's frame must say so rather than look like a second viewpoint.
  const wrongCamera = overlay && overlay.camera !== undefined && overlay.camera !== null
    && Number(overlay.camera) !== Number(cameraId);

  const placeBox = useCallback(
    (nativeBox) => {
      if (!nativeBox || nativeBox.length !== 4 || !frameSize?.w || !frameSize?.h) return null;
      const box = corrector ? corrector.mapBox(nativeBox) : nativeBox;
      if (!box) return null;
      const [x0, y0, x1, y1] = box;
      return {
        left: `${x0 * 100}%`,
        top: `${y0 * 100}%`,
        width: `${Math.max(0, x1 - x0) * 100}%`,
        height: `${Math.max(0, y1 - y0) * 100}%`,
      };
    },
    [frameSize, corrector],
  );

  const overlayBox = useMemo(() => placeBox(overlay?.box), [overlay, placeBox]);

  // What the device is following right now. These are tracked *change
  // regions*, not hands or people — this build has no hand or person
  // detector (measured twice, 0/26 at usable confidence), and labelling a
  // box "hand" when nothing detected a hand teaches the operator to trust a
  // number that does not exist.
  const tracks = overlay?.tracks ?? [];
  // The opening review as it follows the recording (docs/tasks/35): its latest
  // windows on this camera with the product each read as, a pass behind the
  // frame (lag_s). Empty until the cabinet's runtime publishes it.
  const review = overlay?.review ?? null;
  const reviewHits = review?.hits ?? [];
  const nameOf = (id) => names[id]?.name || names[String(id || "").slice(0, 8)]?.name || String(id || "?").slice(0, 8);
  const ignoredRegions = overlay?.ignored ?? [];
  const workArea = overlay?.work_area ?? null;

  // Stable colour per track so the same interaction keeps its colour between
  // frames — the whole point of showing an id.
  const TRACK_COLOURS = ["#34d399", "#60a5fa", "#f472b6", "#fbbf24", "#a78bfa"];
  const trackColour = (id) => TRACK_COLOURS[(id ?? 0) % TRACK_COLOURS.length];

  // Place a normalised point on the stage, in percent.
  const placePoint = useCallback(
    (nativePt) => {
      if (!frameSize?.w || !frameSize?.h) return null;
      const pt = corrector ? corrector.mapPoint(nativePt) : nativePt;
      if (!pt) return null;
      return { x: pt[0] * 100, y: pt[1] * 100 };
    },
    [frameSize, corrector],
  );

  // Lens focus. An M12 lens is focused by turning the barrel, and there is no
  // way to judge that on a headless device — so the runtime measures the
  // detail in the centre of every published frame and this shows it while
  // someone turns the lens. The bands come from the runtime, not from taste:
  // "need" is the same sharpness the analyser requires of a frame before it
  // will let that frame decide which product was taken.
  const focus = overlay?.focus ?? null;
  const focusBox = useMemo(() => placeBox(focus?.box), [focus, placeBox]);
  const focusTone = useMemo(() => {
    switch (focus?.verdict) {
      case "sharp":
        return { ring: "border-emerald-400", chip: "bg-emerald-500/90", note: "sharp" };
      case "usable":
        return { ring: "border-lime-400", chip: "bg-lime-600/90", note: "usable" };
      case "soft":
        return { ring: "border-amber-400", chip: "bg-amber-500/90", note: "too soft to identify" };
      case "no_detail":
        // Nothing textured in view. Blaming the lens here is how a correctly
        // focused camera gets reported as broken.
        return { ring: "border-slate-400", chip: "bg-slate-500/90", note: "nothing to focus on" };
      default:
        return { ring: "border-rose-500", chip: "bg-rose-600/90", note: "no focus" };
    }
  }, [focus]);

  // Motion direction is the honest signal for take vs put-back; the
  // classifier's event_type is forced to product_taken in static mode.
  const directionBadge = useMemo(() => {
    if (review) return { text: `review · ${reviewHits.length} window${reviewHits.length === 1 ? "" : "s"} · ${Number(review.lag_s ?? 0).toFixed(1)} s behind`, tone: "bg-violet-600/90" };
    if (!overlay || overlay.idle) return { text: "live · no review running", tone: "bg-slate-500/80" };
    if (overlay.no_change) return { text: "no product", tone: "bg-slate-500/80" };
    if (overlay.buffering) return { text: "capturing interaction", tone: "bg-indigo-600/80" };
    if (overlay.direction === "out") return { text: "↑ picked up", tone: "bg-emerald-500/90" };
    if (overlay.direction === "in") return { text: "↓ put back", tone: "bg-sky-500/90" };
    if (overlay.event_type === "product_taken") return { text: "picked up (no motion)", tone: "bg-emerald-600/70" };
    if (overlay.event_type === "product_returned") return { text: "put back (no motion)", tone: "bg-sky-600/70" };
    return { text: "no motion", tone: "bg-slate-500/80" };
  }, [overlay]);

  return (
    <div ref={paneRef} className={`relative ${large ? "h-full" : sideways ? "aspect-[3/4]" : "aspect-video"} w-full overflow-hidden rounded border ${large ? "border-slate-700 bg-black" : "border-slate-200 bg-slate-100"} flex items-center justify-center`}>
      {frameUrl ? (
        <>
          <div className="pointer-events-none absolute" style={stageStyle}>
          {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
          <img
            ref={imgRef}
            src={frameUrl}
            alt="device live frame"
            className={corrector ? "hidden" : "h-full w-full object-contain"}
            onLoad={(e) => {
              setFrameSize({
                w: e.currentTarget.naturalWidth,
                h: e.currentTarget.naturalHeight,
              });
              paint();
            }}
          />
          {corrector ? <canvas ref={canvasRef} className="h-full w-full object-contain" /> : null}
          {showBoxes ? (<>
          {overlayBox ? (
            <div
              className={`pointer-events-none absolute rounded-sm border-2 ${
                overlay?.abstain ? "border-amber-400" : "border-emerald-400"
              }`}
              style={overlayBox}
            >
              <span
                className={`absolute -top-5 left-0 whitespace-nowrap rounded px-1 text-[10px] font-medium text-white ${
                  overlay?.abstain ? "bg-amber-500" : "bg-emerald-500"
                }`}
                style={labelStyle}
              >
                {/* While the recognizer is abstaining this box is only a
                    candidate region. Labelling it with a product makes a
                    rejected patch of background read as a detection. */}
                {overlay?.buffering
                  ? "capturing…"
                  : overlay?.abstain
                  ? `no match (${overlay.abstain})`
                  : `${overlay?.label || overlay?.product_uuid?.slice(0, 8) || "object"}${
                      typeof overlay?.cos === "number" ? ` ${overlay.cos.toFixed(2)}` : ""
                    }`}
              </span>
            </div>
          ) : null}
          {/* The analysed zone. Everything outside it is deliberately
              ignored, and saying so is what stops "it did not see me"
              being confused with "it chose not to look there". */}
          {workArea && frameSize ? (
            <svg
              className="pointer-events-none absolute inset-0 h-full w-full"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
            >
              <polygon
                points={workArea
                  .map((pt) => placePoint(pt))
                  .filter(Boolean)
                  .map((p) => `${p.x},${p.y}`)
                  .join(" ")}
                fill="none"
                stroke="#38bdf8"
                strokeOpacity="0.75"
                strokeWidth="0.35"
                strokeDasharray="1.6 1.2"
                vectorEffect="non-scaling-stroke"
              />
            </svg>
          ) : null}

          {/* Trails: where each followed region has been. A box that
              jumps between two interactions and a box that travels look
              identical in a still, and telling them apart is the whole
              question when two people reach in at once. */}
          {frameSize && tracks.some((t) => (t.trail || []).length > 1) ? (
            <svg
              className="pointer-events-none absolute inset-0 h-full w-full"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
            >
              {tracks.map((t) => {
                const pts = (t.trail || []).map(placePoint).filter(Boolean);
                if (pts.length < 2) return null;
                return (
                  <g key={`trail-${t.id}`}>
                    <polyline
                      points={pts.map((p) => `${p.x},${p.y}`).join(" ")}
                      fill="none"
                      stroke={trackColour(t.id)}
                      strokeWidth="0.5"
                      strokeOpacity="0.85"
                      vectorEffect="non-scaling-stroke"
                    />
                    <circle
                      cx={pts[pts.length - 1].x}
                      cy={pts[pts.length - 1].y}
                      r="0.7"
                      fill={trackColour(t.id)}
                      vectorEffect="non-scaling-stroke"
                    />
                  </g>
                );
              })}
            </svg>
          ) : null}

          {/* One box per followed interaction, coloured and numbered so
              the same one keeps its identity across frames. */}
          {tracks.map((t) => {
            const box = placeBox(t.box);
            if (!box) return null;
            return (
              <div
                key={`track-${t.id}`}
                className="pointer-events-none absolute rounded-sm border-2"
                style={{ ...box, borderColor: trackColour(t.id) }}
              >
                <span
                  className="absolute -top-5 left-0 whitespace-nowrap rounded px-1 text-[10px] font-medium text-white"
                  style={{ backgroundColor: trackColour(t.id), ...labelStyle }}
                >
                  #{t.id} · {t.frames} fr. · {t.age_s}s
                </span>
              </div>
            );
          })}

          {/* The review's windows: the product each read as and its cos. A dashed
              box is a product at rest at its place, not moving with a hand. */}
          {reviewHits.map((hit, i) => {
            const box = placeBox(hit.box);
            if (!box) return null;
            const colour = productColour(hit.product);
            return (
              <div key={`hit-${i}`} className={`pointer-events-none absolute rounded-sm border-2 ${hit.at_rest ? "border-dashed" : ""}`}
                style={{ ...box, borderColor: colour }}>
                <span className="absolute -top-5 left-0 whitespace-nowrap rounded px-1 text-[10px] font-medium text-slate-900" style={{ backgroundColor: colour, ...labelStyle }}>
                  {nameOf(hit.product)} {typeof hit.cos === "number" ? hit.cos.toFixed(2) : ""}{hit.src === "motion" ? " · motion" : ""}
                </span>
              </div>
            );
          })}

          {/* Movement the work area excluded — a person walking past, a
              chair moved. Shown, never analysed. */}
          {ignoredRegions.map((b, i) => {
            const box = placeBox(b);
            if (!box) return null;
            return (
              <div
                key={`ignored-${i}`}
                className="pointer-events-none absolute rounded-sm border border-dashed border-slate-400/70"
                style={box}
              >
                <span className="absolute -top-4 left-0 whitespace-nowrap rounded bg-slate-700/80 px-1 text-[9px] text-white" style={labelStyle}>
                  outside the zone - not analysed
                </span>
              </div>
            );
          })}

          {/* Focus target. Drawn on the same square the device measures,
              so what you point at a label is what the number reports. */}
          {focus && focusBox ? (
            <div
              className={`pointer-events-none absolute rounded border-2 border-dashed ${focusTone.ring}`}
              style={focusBox}
            >
              <span
                className={`absolute -bottom-5 left-0 whitespace-nowrap rounded px-1 text-[10px] font-medium text-white ${focusTone.chip}`}
                style={labelStyle}
              >
                focus {focus.sharp} — {focusTone.note}
              </span>
            </div>
          ) : null}
          </>) : null}
          </div>
          {wrongCamera ? (
            <div className="pointer-events-none absolute inset-x-2 bottom-2 rounded bg-rose-600 px-2 py-1 text-[11px] font-medium text-white">
              this frame says camera {String(overlay.camera)}, not camera {cameraId} — the cabinet published it under the wrong camera
            </div>
          ) : null}
          {stale && !large ? (
            <div className="pointer-events-none absolute inset-x-2 bottom-2 rounded bg-rose-600/90 px-2 py-1 text-[11px] font-medium text-white">
              no new frame for {Math.round(stale.age)} s — {stale.why}
            </div>
          ) : null}
          {showText ? (
          <div className="pointer-events-none absolute left-2 top-2 flex flex-col gap-1">
            <span className={`rounded px-2 py-0.5 text-[11px] font-medium text-white ${directionBadge.tone}`}>
              {directionBadge.text}
            </span>
            {corrector ? (
              <span className="rounded bg-teal-700/90 px-2 py-0.5 text-[11px] text-white">
                corrected view · {corrector.lens ? `straightened ${corrector.hfovDeg}°` : "fisheye kept"}
                {corrector.lut ? " · white balance" : ""} · display only
              </span>
            ) : null}
            {focus ? (
              <span className={`rounded px-2 py-0.5 text-[11px] font-medium text-white ${focusTone.chip}`}>
                lens focus {focus.sharp} / need {focus.need}
                {focus.verdict === "unfocused"
                  ? " — turn the lens barrel"
                  : focus.verdict === "soft"
                  ? " — keep turning"
                  : focus.verdict === "no_detail"
                  ? " — point it at something with fine print"
                  : ""}
              </span>
            ) : null}
            {overlay?.no_change ? (
              <span className="rounded bg-slate-900/70 px-2 py-0.5 text-[11px] text-white">
                matches background — not analysed
              </span>
            ) : null}
            {overlay?.buffering ? (
              // During an interaction nothing has been scored yet, so
              // cos/margin/votes would all read 0.00 and look broken.
              <span className="rounded bg-indigo-600/80 px-2 py-0.5 text-[11px] text-white">
                capturing… {overlay.buffered ?? 0} frames buffered
              </span>
            ) : overlay && !overlay.idle ? (
              <span className="rounded bg-slate-900/70 px-2 py-0.5 text-[11px] text-white">
                cos {Number(overlay.cos ?? 0).toFixed(2)} · margin{" "}
                {Number(overlay.margin ?? 0).toFixed(2)}
                {overlay.stages === "episode" ? " · episode" : ""}
                {overlay.abstain ? ` · ${overlay.abstain}` : ""}
              </span>
            ) : null}
          </div>
          ) : null}
        </>
      ) : (
        <span className="text-sm text-slate-500">waiting for first frame…</span>
      )}
      {/* Which camera this is. With two feeds side by side, an unlabelled
          pane is worse than no pane: a box drawn on the wrong viewpoint is
          indistinguishable from a box drawn correctly. */}
      {showText ? (
        <span className="pointer-events-none absolute right-2 top-2 rounded bg-slate-900/70 px-2 py-0.5 text-[11px] font-medium text-white">
          {label || `camera ${cameraId}`}
        </span>
      ) : null}
      {onExpand && frameUrl ? (
        <button
          type="button"
          onClick={onExpand}
          title="Open this camera full screen (texts and boxes can be hidden there)"
          aria-label={`Open ${label || `camera ${cameraId}`} full screen`}
          className="absolute right-2 top-8 rounded bg-slate-900/70 px-1.5 py-0.5 text-[13px] leading-none text-white hover:bg-slate-900"
        >
          ⤢
        </button>
      ) : null}
      {/* The corrected view exists only for a cabinet that was measured
          (docs/tasks/39): the button appears with its calibration. */}
      {onToggleCorrected && calibration && frameUrl ? (
        <button
          type="button"
          onClick={onToggleCorrected}
          title="Corrected view: the fisheye straightened and the white balance neutralised from the cabinet's measured lens and light. Display only - the cabinet keeps working on the native frame."
          aria-pressed={corrected}
          className={`absolute right-2 ${onExpand ? "top-14" : "top-8"} rounded px-1.5 py-0.5 text-[10px] font-medium leading-none ${corrected ? "bg-teal-500 text-white" : "bg-slate-900/70 text-white hover:bg-slate-900"}`}
        >
          {corrected ? "corrected" : "native"}
        </button>
      ) : null}
      {/* The review's current windows as crops, cut in the browser from this
          same frame (no image leaves the cabinet for them). */}
      {frameUrl && frameSize && showText && reviewHits.length > 0 ? (
        <div className="absolute inset-x-0 bottom-0 flex gap-1 overflow-x-auto bg-slate-900/60 p-1">
          {reviewHits.slice(0, 8).map((hit, i) => {
            const [x0, y0, x1, y1] = hit.box || [];
            const bw = Math.max(0.01, x1 - x0), bh = Math.max(0.01, y1 - y0);
            const side = 56;
            return (
              <div key={`crop-${i}`} className="shrink-0 text-center">
                <div className="rounded-sm border-2" title={`${nameOf(hit.product)} ${hit.cos}`}
                  style={{
                    width: side, height: side, borderColor: productColour(hit.product),
                    backgroundImage: `url("${frameUrl}")`,
                    backgroundSize: `${100 / bw}% ${100 / bh}%`,
                    backgroundPosition: `${bw >= 1 ? 0 : (x0 / (1 - bw)) * 100}% ${bh >= 1 ? 0 : (y0 / (1 - bh)) * 100}%`,
                    transform: turn ? `rotate(${turn}deg)` : undefined,
                  }} />
                <div className="max-w-[56px] truncate text-[9px] text-white">{typeof hit.cos === "number" ? hit.cos.toFixed(2) : ""}</div>
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
